import {
    ApplicationModelId,
    ArtifactId,
    AuditId,
    BlobId,
    BrowserSessionStateId,
    DeviceId,
    ExpeditionId,
    PackageId,
    ProjectId,
    QASessionAssociationId,
    QASessionId,
    ReportSpecId,
    ScreenshotId,
    SpecGroupId,
    SpecId,
    StateCollectionId,
    StateCollectorJobId,
    StateCollectorSpecId,
    TestExecutionId,
    FindingId,
    TestRunId,
    TestSessionId,
    TestTemplateId,
    UserId, TestEmailAccountId, TestObjectCollectionId, TestObjectId, TestSessionCellId, VideoRecordingId
} from '@webmate-web-core/shared/types/ids';
import {DateTime, Progress, RemoteError, WMValue} from '@webmate-web-core/shared/types/base-types';
import {Tag} from '@webmate-web-core/shared/types/tag';
import {Browser} from '@webmate-web-core/shared/types/browser';
import {AutomationPolicyConfig, ElementDescriptor, InteractionPath} from '@webmate-web-core/shared/types/browsersession';
import {FindingSearchRecord} from '@webmate-web-core/projects/project/testlab/project-findings.component';
import {DeviceRequirement} from '@webmate-web-core/shared/types/crawler';

export type TestType =
    'selenium'
    | 'expedition_comparison'
    | 'crossbrowser_comparison'
    | 'session_comparison'
    | 'legacy_crossbrowser_singleurl_comparison'
    | 'legacy_crossbrowser_comparison'
    | 'legacy_regression_comparison'
    | 'story_check'
    | 'spec_check'
    | 'externally_controlled'
    | 'adhoc';
export type ParameterName = string;
export type FindingType = string;

export type VehicleSpecificationType = 'BrowserSpecification' | 'AppSpecification' | 'EmptySpecification';
export type TestRunCreationSpecType = 'SingleTestRunCreationSpec';
export type DriverSpecificationType =
    'InteractiveDriverSpecification'
    | 'InteractionPathDriverSpecification'
    | 'URLListDriverSpecification'
    | 'ClickDriverSpecification'
    | 'SequentialDriverSpecification'
    | 'SessionReplayDriverSpecification'
    | 'WebDriverScriptDriverSpecification'
    | 'AppiumDriverSpecification';

export type DriverSpecification = InteractiveDriverSpecification | InteractionPathDriverSpecification | URLListDriverSpecification | SequentialDriverSpecification | ClickDriverSpecification | AppiumDriverSpecification;
export type VehicleSpecification = BrowserSpecification;
export type TestRunCreationSpec = SingleTestRunCreationSpec;

export type TestExecutionExecutionStatus = string;
export type TestExecutionEvaluationStatus = string;

// known event types in the testmgmt domain
export const TestMgmtEventTypes = {
    FindingAdded: 'TestMgmt.Finding.Added',
    FindingUpdated: 'TestMgmt.Finding.Updated',
    FindingDeleted: 'TestMgmt.Finding.Deleted',
    FindingIndexUpdated: 'TestMgmt.Finding.IndexUpdated',
    TestObjectCollectionCreated: 'TestMgmt.TestObjectCollection.Created',
    TestObjectCollectionUpdated: 'TestMgmt.TestObjectCollection.Updated',
    TestObjectCollectionMembershipChanged: 'TestMgmt.TestObjectCollection.MembershipChanged',
    TestObjectCollectionArchived: 'TestMgmt.TestObjectCollection.Archived',
    TestObjectCollectionActivated: 'TestMgmt.TestObjectCollection.Activated',
    TestObjectCollectionDeleted: 'TestMgmt.TestObjectCollection.Deleted',
}

export type QASessionStatus = 'Open' | 'Closed' | 'Archived';

export type QASessionAssociationTargetType =
    'Artifact'
    | 'Finding'
    | 'TestRun'
    | 'BrowserSession'
    | 'Expedition'
    | 'Spec'
    | 'TestObject'
    | 'Report'
    | 'Audit'
    | 'ExternalUpload'
    | 'Comment'
    | 'QATask'
    | 'Other';

export type QASessionAssociationRelationType =
    'Contains'
    | 'ProducedIn'
    | 'ImportedInto'
    | 'EvidenceFor'
    | 'Discusses'
    | 'GeneratedFrom'
    | 'RelatedTo'
    | 'Supersedes';

export type QASessionMetadata = {[key: string]: any};

export interface QASession {
    id: QASessionId,
    projectId: ProjectId,
    title: string,
    description?: string,
    status: QASessionStatus,
    createdBy: UserId,
    createdAt: DateTime,
    updatedAt: DateTime,
    closedAt?: DateTime,
    metadata: QASessionMetadata
}

export interface QASessionAssociation {
    id: QASessionAssociationId,
    sessionId: QASessionId,
    projectId: ProjectId,
    targetType: QASessionAssociationTargetType,
    targetId: string,
    relationType: QASessionAssociationRelationType,
    addedBy: UserId,
    addedAt: DateTime,
    source?: string,
    metadata: QASessionMetadata
}

export interface CreateQASessionData {
    title: string,
    description?: string,
    metadata?: QASessionMetadata
}

export interface UpdateQASessionData {
    title?: string,
    description?: string,
    status?: QASessionStatus,
    metadata?: QASessionMetadata
}

export interface CreateQASessionAssociationData {
    targetType: QASessionAssociationTargetType,
    targetId: string,
    relationType?: QASessionAssociationRelationType,
    source?: string,
    metadata?: QASessionMetadata
}

export interface TestTemplate {
    info: TestInfo,
    testType: TestType,
    models: Array<ApplicationModelId>,
    params: Array<TestParameter>
}

export interface CreateSpecDTO {
    title: string,
    description: string,
    labels?: Tag[],
    displayId?: string,
    groupId?: SpecGroupId,
    groupOrder?: number
}

export interface CreateSpecGroupDTO {
    displayId?: string,
    name: string,
    description: string,
    parentGroup?: SpecGroupId,
    groupOrder?: number
}

export interface TestInfo {
    id: TestTemplateId,
    name: string,
    creationTime: any,
    description: string,
    version: number,
    isBaseTemplate: boolean
}

export interface TestParameter {
    name: string,
    parameterType: string,
    required: boolean,
    optDescription?: string,
    optDisplayName?: string,
    default?: WMValue<any>
}

export interface FrameworkProvenance {
    name: string,
    version?: string
}

export interface ReporterProvenance {
    name: string,
    version?: string
}

export interface CIProvenance {
    provider: string,
    runId?: string,
    jobId?: string,
    url?: string
}

export interface ExecutionProvenance {
    origin: 'external' | 'webmate',
    framework?: FrameworkProvenance,
    reporter?: ReporterProvenance,
    ci?: CIProvenance
}

export interface TestExecutionSummary {
    id: TestExecutionId,
    testId: TestTemplateId,
    name: string,
    version: number,
    creator: UserId,
    executionStatus: TestExecutionExecutionStatus,
    evaluationStatus: TestExecutionEvaluationStatus,
    creationTime: Date,
    spec: TestRunCreationSpec,
    displayType?: string,
    provenance?: ExecutionProvenance
}

export interface AssignmentSpec {
    [s: ParameterName]: WMValue<any>;
};

export interface SingleTestRunCreationSpec {
    type: TestRunCreationSpecType,
    assignmentSpec: AssignmentSpec;
}

export type TestRunEvaluationStatus = string;
export type TestRunExecutionStatus = string;

export interface TestRunDeviceInfo {
    id: DeviceId;
    creationTime: Date;
    name: string;
    request: any;
    properties: any;
    projectId: ProjectId;
}


export interface TestRunStatusInfo {
    evaluationStatus: TestRunEvaluationStatus,
    executionStatus: TestRunExecutionStatus,
    deleted: boolean,
    progress: number,
    progressMessage?: string,
    failure?: RemoteError
}

export interface TestRun {
    testRunId: TestRunId
    name: string,
    testExecutionId: TestExecutionId
    creator: UserId
    startTime: DateTime | undefined
    endTime: DateTime | undefined
    lastUpdateTime: DateTime | undefined,
    auditId?: AuditId,
    output: any,
    evaluationStatus: TestRunEvaluationStatus,
    projectId: ProjectId,
    executionStatus: TestRunExecutionStatus,
    testType: TestType,
    issueSummary: IssueStatistics
    expeditions: ExpeditionId[],
    devices: TestRunDeviceInfo[],
    failure: RemoteError | undefined,
}


export interface TestRunReport {
    testRunId: TestRunId
    name: string,
    projectId: ProjectId,
    executionId: TestExecutionId
    creator: UserId
    creationTime: DateTime | undefined
    endTime: DateTime | undefined
    lastUpdateTime: DateTime | undefined,
    output: any,
    evaluationStatus: TestRunEvaluationStatus,
    executionStatus: TestRunExecutionStatus,
    testType: TestType,
    issueSummary: IssueStatistics
    expeditions: ExpeditionId[],
    devices: DeviceId[],
    sessions: TestSessionId[],
    testId: TestTemplateId | undefined
}

export interface TestRunReportConfig {
    reportName?: string;
    executiveSummary?: string;
    severityFilterSpec?: FindingSeverity[];
    language?: string;
    includeRawOutput?: boolean;
    maxFindings?: number;
}

export interface Spec {
    id: SpecId,
    displayId?: string,
    version: number,
    projectId: ProjectId,
    title: string,
    description: string,
    groupId?: SpecGroupId,
    groupOrder?: number,
    labels: Tag[],
    checkers: any[],
    creator: UserId,
    createdOn: DateTime,
    lastUpdater: UserId,
    lastUpdated: DateTime,
    lastUpdateAction: string
}

export interface SpecGroup {
    id: SpecGroupId,
    displayId?: string,
    name: string,
    description: string,
    parent?: SpecGroupId,
    groupOrder?: number,
    projectId: ProjectId,
    creator: UserId,
    creationTime: DateTime,
    lastUpdater: UserId,
    lastUpdated: DateTime

}

export interface SpecFilterSpec {
    labels?: Tag[],
    nameSearchString?: string,
    descendentOf?: SpecGroupId,
}

export interface SpecSetName {
    type: 'SetName',
    name: string
}

export interface SpecSetDescription {
    type: 'SetDescription',
    description: string
}

export interface SpecSetLabels {
    type: 'SetLabels',
    labels: Tag[]
}

export interface SpecSetDisplayId {
    type: 'SetDisplayId',
    displayId: string
}

export type SpecUpdateAction = SpecSetName | SpecSetDescription | SpecSetLabels | SpecSetDisplayId

export interface SpecGroupSetName {
    type: 'SetName',
    name: string
}

export interface SpecGroupSetDescription {
    type: 'SetDescription',
    description: string
}

export interface SpecGroupSetDisplayId {
    type: 'SetDisplayId',
    displayId: string
}

export interface SpecGroupSetLabels {
    type: 'SetLabels',
    labels: Tag[]
}

export type SpecGroupUpdateAction = SpecGroupSetName | SpecGroupSetDescription | SpecGroupSetDisplayId | SpecGroupSetLabels;

export class TestRunHelpers {
    static isPassed(evaluationStatus: TestRunEvaluationStatus): boolean {
        return evaluationStatus === 'passed';
    }

    static isPending(executionStatus: TestRunExecutionStatus, evaluationStatus: TestRunEvaluationStatus): boolean {
        return executionStatus !== 'completed' && evaluationStatus.startsWith('pending');
    }

    static isFinalizing(executionStatus: TestRunExecutionStatus, evaluationStatus: TestRunEvaluationStatus): boolean {
        return executionStatus === 'stopped'
            && (this.isPendingPassed(evaluationStatus) || this.isPendingFailed(evaluationStatus));
    }

    static isPendingPassed(evaluationStatus: TestRunEvaluationStatus): boolean {
        return evaluationStatus === 'pending_passed';
    }

    static isPendingFailed(evaluationStatus: TestRunEvaluationStatus): boolean {
        return evaluationStatus === 'pending_failed';
    }

    static isFailed(evaluationStatus: TestRunEvaluationStatus): boolean {
        return evaluationStatus === 'failed';
    }

    static isSkipped(evaluationStatus: TestRunEvaluationStatus): boolean {
        return evaluationStatus === 'skipped';
    }

    static isRunning(executionStatus: TestRunExecutionStatus): boolean {
        return executionStatus === 'running';
    }

    static isError(executionStatus: TestRunExecutionStatus): boolean {
        return executionStatus === 'failed';
    }

    static canFinishTestRun(testType: TestType) {
        return testType === 'adhoc';
    }

}

export interface IssueStatistics {
    aggregatedIssueCountBySeverity: any;
    issueTypeSummaries: any;
}

export type ObservationTargetHash = string;

export interface ObservationViewport {
    width: number;
    height: number;
}

export interface ObservationEnvironment {
    vehicle?: any;
    viewport?: ObservationViewport;
    packageId?: PackageId;
    tags?: Tag[];
    attributes?: {[key: string]: any};
}

export interface ObservationTarget {
    type: string;
    name?: string;
    description?: string;
    screenshot?: string;
    labels?: Tag[];
    testObjectId?: TestObjectId;
    environment?: ObservationEnvironment;
}

export interface ObservationTargetListSourceSpec {
    type: 'ObservationTargetListSourceSpec',
    observationTargets: ObservationTarget[]
}

export interface URLListSourceSpec {
    type: 'URLListSourceSpec',
    urls: string[]
}

export interface CrawledSourceSpec {
    type: 'CrawledSourceSpec',
    seed: {
        urls: NamedUrl[],
        useSitemap: boolean
    },
    config: ExplorationConfig
}

export interface TestObjectListSourceSpec {
    type: 'TestObjectListSourceSpec',
    testObjectIds: string[]
}

export interface TestObjectCollectionSourceSpec {
    type: 'TestObjectCollectionSourceSpec',
    specCollectionId: string
}

export type TestObjectsSourceSpec =
    ObservationTargetListSourceSpec
    | URLListSourceSpec
    | CrawledSourceSpec
    | TestObjectListSourceSpec
    | TestObjectCollectionSourceSpec;

export interface TestObjectResolutionJobProgress {
    progressMessage: string,
    progress: Progress,
    failureMsg?: string
}

export interface TestObjectResolutionJob {
    id: string,
    creationTime: DateTime,
    lastUpdateTime: DateTime,
    creator: UserId,
    sourceSpec: TestObjectsSourceSpec,
    resolvedTestObjectIds: string[],
    progress: TestObjectResolutionJobProgress,
    progressInfo?: any,
    projectId: ProjectId
}



export interface TestObject {
    id: string,
    name: string,
    description?: string,
    generationSpec: any,
    matchingSpec: any,
    automationPolicy?: AutomationPolicyConfig | null,
    labels?: Tag[],
    origin: TestObjectOrigin,
    projectId: ProjectId,
    creator: UserId,
    creationTime: DateTime,
    lastUpdater: UserId,
    lastUpdateTime: DateTime,
    lastUpdateAction: string,
    version: number
}

export type TestObjectOrigin =
    'unknown'
    | 'manual'
    | 'url_list'
    | 'crawled'
    | 'observation_target';

export interface ManualGenerationSpec {
    type: 'ManualGenerationSpec',
    instructions: string,
    environment?: any
}

export interface MobileAppGenerationSpec {
    type: 'MobileAppGenerationSpec',
    packageId: PackageId,
    environment?: any
}

export interface NoAutomaticMatchingSpec {
    type: 'NoAutomaticMatchingSpec'
}

export interface URLMatchingSpec {
    type: 'URLMatchingSpec',
    urlRegex: string
}

export interface CreateTestObjectDTO {
    id?: TestObjectId,
    name: string,
    description?: string,
    generationSpec: any,
    matchingSpec?: any,
    automationPolicy?: AutomationPolicyConfig | null,
    labels?: Tag[]
}

export interface OpenTestObjectInExpeditionRequest {
    expeditionId: ExpeditionId;
}

export interface TestObjectStateSummary {
    stateId: BrowserSessionStateId,
    name: string,
    creationTime: DateTime,
    browserSessionId: ExpeditionId,
    testRunId?: TestRunId
}

export type TestObjectCollectionMemberRef = string;

export type TestObjectCollectionStatus =
    'active'
    | 'archived'
    | 'deleted';

export interface TestObjectCollectionSummary {
    collectionId: TestObjectCollectionId,
    projectId: ProjectId,
    name: string,
    description?: string,
    memberCount: number,
    status: TestObjectCollectionStatus
}

export interface TestObjectCollection {
    collectionId: TestObjectCollectionId,
    version: number,
    projectId: ProjectId,
    name: string,
    description?: string,
    members: TestObjectCollectionMemberRef[],
    status: TestObjectCollectionStatus,
    createdAt: DateTime,
    createdBy: UserId,
    updatedAt: DateTime,
    updatedBy: UserId,
    lastUpdateAction: string
}

export interface CreateTestObjectCollectionDTO {
    collectionId?: TestObjectCollectionId,
    name: string,
    description?: string,
    members?: TestObjectCollectionMemberRef[]
}

export interface TestObjectCollectionSetNameAction {
    type: 'SetName',
    name: string
}

export interface TestObjectCollectionSetDescriptionAction {
    type: 'SetDescription',
    description?: string
}

export interface TestObjectCollectionAddMemberAction {
    type: 'AddMember',
    member: TestObjectCollectionMemberRef
}

export interface TestObjectCollectionRemoveMemberAction {
    type: 'RemoveMember',
    member: TestObjectCollectionMemberRef
}

export interface TestObjectCollectionReorderMembersAction {
    type: 'ReorderMembers',
    members: TestObjectCollectionMemberRef[]
}

export interface TestObjectCollectionArchiveAction {
    type: 'Archive'
}

export interface TestObjectCollectionActivateAction {
    type: 'Activate'
}

export type TestObjectCollectionUpdateAction =
    | TestObjectCollectionSetNameAction
    | TestObjectCollectionSetDescriptionAction
    | TestObjectCollectionAddMemberAction
    | TestObjectCollectionRemoveMemberAction
    | TestObjectCollectionReorderMembersAction
    | TestObjectCollectionArchiveAction
    | TestObjectCollectionActivateAction;


export interface StateObservationTarget extends ObservationTarget {
    type: 'StateObservationTarget',
    stateId: BrowserSessionStateId
}

export interface URLObservationTarget extends ObservationTarget {
    type: 'URLObservationTarget',
    url: string
}

export interface TagObservationTarget extends ObservationTarget {
    type: 'TagObservationTarget',
    tag: Tag
}

export interface ContextObservationTarget extends ObservationTarget {
    type: 'ContextObservationTarget'
}

function canonicalizeObservationTarget(target?: ObservationTarget): any {
    if (!target) {
        return target;
    }

    const clone = JSON.parse(JSON.stringify(target));
    delete clone.testObjectId;
    if (clone.element && typeof clone.element === 'object') {
        delete clone.element.testObjectId;
    }
    return clone;
}

export function observationTargetEquals(a?: ObservationTarget, b?: ObservationTarget): boolean {
    if (!a || !b || a.type !== b.type) {
        return false;
    }

    return JSON.stringify(canonicalizeObservationTarget(a)) === JSON.stringify(canonicalizeObservationTarget(b));
}

// private cache for observation target hashes
const observationTargetHashCache: { [s: string]: ObservationTargetHash } = {};

export async function computeObservationTargetHashes(targets: ObservationTarget[]): Promise<ObservationTargetHash[]> {
    return Promise.all(targets.map(target => computeObservationTargetHash(target)));
}

/**
 * Returns a hash for the given observation target. This hash is used to identify the target in the frontend.
 */
export async function computeObservationTargetHash(target: ObservationTarget): Promise<ObservationTargetHash> {
    const cacheKey = JSON.stringify(target);
    if (observationTargetHashCache[cacheKey]) {
        return observationTargetHashCache[cacheKey];
    }

    // TODO: we should make this deterministic, because JSON serialization may differ between browser and backend
    // let str: string;
    // if (target.type === 'StateObservationTarget') {
    //     str = (target as StateObservationTarget).stateId;
    // } else if (target.type === 'TagObservationTarget') {
    //     str = JSON.stringify((target as TagObservationTarget).tag);
    // } else {
    //     str = '';
    // }

    const data = new TextEncoder().encode(JSON.stringify(target));
    const buf = await crypto.subtle.digest('SHA-1', data);
    const bytes = new Uint8Array(buf);
    return Array.from(bytes).map(b => b.toString(16).padStart(2, '0')).join('');
}

export function deriveObservationEnvironmentFromGenerationSpec(generationSpec: any): ObservationEnvironment | undefined {
    const generationEnvironment = generationSpec?.environment;
    if (!generationEnvironment || generationEnvironment.type !== 'VehicleEnvironmentSpec' || !generationEnvironment.vehicle) {
        return undefined;
    }

    const mapped: ObservationEnvironment = {
        vehicle: generationEnvironment.vehicle,
    };

    const viewport = generationEnvironment.vehicle.viewPort ?? generationEnvironment.vehicle.viewport;
    if (viewport?.width != null && viewport?.height != null) {
        mapped.viewport = {
            width: viewport.width,
            height: viewport.height,
        };
    }

    if ((generationEnvironment.vehicle.type === 'AndroidAppSpecification' || generationEnvironment.vehicle.type === 'IOSAppSpecification') && generationEnvironment.vehicle.appId) {
        mapped.packageId = generationEnvironment.vehicle.appId;
    }

    return mapped;
}

export type FindingSeverity = 'good' | 'info' | 'warn' | 'error' | 'not_set';

export const WellknownSeverities: FindingSeverity[] = ['good', 'info', 'warn', 'error', 'not_set'];

export interface Finding {
    id: FindingId,
    version: number,
    title?: string,
    testRunId: TestRunId,
    creationTime: DateTime,
    creator: UserId,
    projectId: ProjectId,
    severity: FindingSeverity,
    cells: {[id: string]: ContentCell},
    cellSequence: ContentCellId[],
    issueType: FindingType,
    properties: any,
    associatedArtifacts: Array<ArtifactId>,
    affectedSpecs?: SpecId[],
    observationTargets?: ObservationTarget[],
    testRun?: TestRunId,
    auditId?: AuditId,
    tags: Array<Tag>,
}

export interface FindingsStats {
    'good'?: number,
    'info'?: number,
    'warn'?: number,
    'error'?: number,
    total?: number
}

/**
 * A finding without any content
 */
export interface FindingInfo {
    id: FindingId,
    version: number,
    title?: string,
    creationTime: DateTime,
    creator: UserId,
    projectId: ProjectId,
    severity: FindingSeverity,
    issueType: FindingType,
    associatedArtifacts: Array<ArtifactId>,
    affectedSpecs?: SpecId[],
    observationTargets?: ObservationTarget[],
    testRun?: TestRunId,
    auditId?: AuditId,
    tags: Array<Tag>
}

/**
 * Best effort conversion from search record to finding info
 */
export function searchRecordToFindingInfo(r: FindingSearchRecord): FindingInfo {
    return {
        id: r.id,
        version: r.version,
        title: r.title || undefined,
        creationTime: r.createdOn,
        creator: r.creator,
        projectId: r.projectId,
        severity: r.severity as FindingSeverity,
        issueType: r.issueType,
        affectedSpecs: r.affectedSpecsInfos.map(i => i.specId),
        associatedArtifacts: r.associatedArtifacts,
        auditId: r.auditId,
        testRun: r.testRun?.id,
        observationTargets: r.observationTargets,
        tags: r.tags || []
  };
}

export type ObservationData = {
    type: 'ArtifactObservation',
    artifactId: ArtifactId
} | {
    type: 'ScreenshotObservation',
    screenshotId: ScreenshotId,
    artifactId?: ArtifactId,
    region?: {x: number, y: number, width: number, height: number}
} | {
    type: 'AttachmentObservation',
    blobId: BlobId,
    name: string,
    mimeType?: string,
    sizeBytes?: number,
} | {
    type: 'WCAGReportObservation',
    reportArtifact: ArtifactId,
    elements?: ElementDescriptor[],
    requirements?: SpecId[],
    summary: any
} | {
    type: 'VideoSnippetObservation',
    videoId: VideoRecordingId,
    artifactId?: ArtifactId,
} | {
    type: 'TestMailObservation',
    artifactId: ArtifactId
}

export type ContentCell = {
    type: 'TextContentCell',
    text: string
} | {
    type: 'ObservationContentCell',
    observation: ObservationData
}

export type ContentCellId = string;

export interface FindingFindingData {
    cells: {[id: string]: any},
    cellSequence: string[],
    title?: string,
}

export interface FindingUpdateData {
    findingType?: FindingType;
    issueData?: any;
    deleted?: boolean;
    severity?: string;
    associatedArtifacts?: ArtifactId[];
    affectedSpecs?: SpecId[];
    cellSequence?: ContentCellId[];
    cells?: {[id: string]: ContentCell};
    observationTargets?: ObservationTarget[];
    testRun?: TestRunId | null;
    audit?: AuditId | null;
    title?: string;
}

export interface FindingCreationData {
    id?: FindingId,
    findingType?: FindingType;
    issueData?: any;
    severity?: string;
    associatedArtifacts?: ArtifactId[];
    affectedSpecs?: SpecId[];
    observationTargets?: ObservationTarget[];
    cellSequence?: ContentCellId[];
    cells?: {[id: string]: ContentCell};
    testRun?: TestRunId;
    audit?: AuditId;
    title?: string;
}


export function findingIsError(result: Finding): boolean {
    return result.severity === 'error';
}

export type ExpeditionSpec = LiveExpeditionSpec | OfflineExpeditionSpec;

export interface BasicAuthInfo {
    basic_auth_domain: string;
    basic_auth_password: string;
    basic_auth_username: string;
}

export interface LiveExpeditionSpec {
    type: string
    driverSpec: DriverSpecification,
    vehicleSpec: VehicleSpecification,
    basicAuth?: Array<BasicAuthInfo>,
    requestedFacts?: FactRequest[],
    automationPolicy?: AutomationPolicyConfig,
    sticky?: boolean
}

export interface OfflineExpeditionSpec {
    type: string,
    expeditionId: ExpeditionId
}

export interface URLListDriverSpecification {
    type: DriverSpecificationType;
    urls: string[];
}

export interface InteractiveDriverSpecification {
    type: DriverSpecificationType;
    startUrl?: string;
}

export interface InteractionPathDriverSpecification {
    type: DriverSpecificationType;
    interactionPaths: InteractionPath[];
}

export interface SequentialDriverSpecification {
    type: DriverSpecificationType;
    specs: DriverSpecification[];
}

export interface ClickDriverSpecification {
    type: DriverSpecificationType;
    specs: { url: string, xpaths: string[] }[];
}

export interface AppiumDriverSpecification {
    type: DriverSpecificationType;
    deviceId: DeviceId;
}

export interface BrowserSpecification {
    type: VehicleSpecificationType
    browser: Partial<Browser>,
    useProxy: boolean,
    additionalDeviceRequirements?: DeviceRequirement,
    headless?: boolean,
    needCDP?: boolean
}

// Frontend specific wrapper types
export interface ExpeditionComparisonTestConfig {
    name?: string,
    description?: string,
    referenceExpeditionSpec?: WMValue<ExpeditionSpec>,
    comparisonExpeditionSpecs?: WMValue<ExpeditionSpec[]>,
    testVisualisationType?: WMValue<string>
}

export interface SpecCheckTestConfig {
    name?: string,
    description?: string,
    testObjects: WMValue<TestObject[]>,
    specs: WMValue<SpecId[]>,
    planningStrategy?: WMValue<any>,
    automationPolicy?: AutomationPolicyConfig
}

export interface GenericTestConfig {
    name?: string,
    description?: string
}

export function expeditionComparisonParamsFromConfig(c: ExpeditionComparisonTestConfig): AssignmentSpec {
    let params: AssignmentSpec = {};
    if (c.referenceExpeditionSpec) params['referenceExpeditionSpec'] = c.referenceExpeditionSpec;
    if (c.comparisonExpeditionSpecs) params['comparisonExpeditionSpecs'] = c.comparisonExpeditionSpecs;
    if (c.testVisualisationType) params['testVisualisationType'] = c.testVisualisationType;
    return params;
}

export function defaultExpeditionComparisonConfigFromTest(t: TestTemplate): ExpeditionComparisonTestConfig {
    let defRef = t.params.find(p => p.name === 'referenceExpeditionSpec')!.default;
    let defComp = t.params.find(p => p.name === 'comparisonExpeditionSpecs')!.default;
    let defVis = t.params.find(p => p.name === 'testVisualisationType')!.default;
    return {
        name: t.info.name,
        description: t.info.description,
        referenceExpeditionSpec: defRef as WMValue<ExpeditionSpec>,
        comparisonExpeditionSpecs: defComp as WMValue<ExpeditionSpec[]>,
        testVisualisationType: defVis as WMValue<string>
    };
}

export function specCheckParamsFromConfig(c: SpecCheckTestConfig): AssignmentSpec {
    let params: AssignmentSpec = {};
    if (c.specs) params['specs'] = c.specs;
    if (c.testObjects) params['testObjects'] = c.testObjects;
    if (c.planningStrategy) params['planningStrategy'] = c.planningStrategy;
    return params;
}

export function defaultSpecCheckConfigFromTest(t: TestTemplate): SpecCheckTestConfig {
    console.log('Extracting default config for test', t);
    let defTestObjects = t.params.find(p => p.name === 'testObjects')!.default;
    let defSpecs = t.params.find(p => p.name === 'specs')!.default;
    let defPlanningStrategy = t.params.find(p => p.name === 'planningStrategy')!.default;
    return {
        name: t.info.name,
        description: t.info.description,
        testObjects: defTestObjects as WMValue<TestObject[]>,
        specs: defSpecs as WMValue<SpecId[]>,
        planningStrategy: defPlanningStrategy as WMValue<any>
    };
}

export type TestSessionExecutionStatus = string;
export type TestSessionEvaluationStatus = string;

export interface TestSession {
    id: TestSessionId,
    creationTime: Date | undefined,
    creator: UserId,
    executionStatus: TestSessionEvaluationStatus,
    evaluationStatus: TestSessionEvaluationStatus,
    projectId: ProjectId,
    name: string,
    endTime: Date | undefined,
    activities: any[],
    participants: UserId[]
}

export interface FilterSpec {
    timeSpec?: any,
    tagFilterSpecs?: any,
    testTypeFilterSpec?: string[],
    testTemplateFilterSpec?: any,
    userFilterSpec?: any,
    statusFilterSpec?: any,
    deviceFilterSpecs?: any[],
    testRunFilterSpec?: any[],
    testNameFilterSpec?: string,
}
export type GroupingSpec = string[];

export interface FindingFilterSpec {
    timeSpec?: any,
    tagFilterSpecs?: any,
    findingTypeFilterSpec?: any,
    userFilterSpec?: any,
    severityFilterSpec?: any,
    observationTargetTitlesSpec?: any[],
    observationTargetHashes?: ObservationTargetHash[],
    affectedSpecTitlesSpec?: any[],
    affectedSpecs?: SpecId[],
    testObjectIds?: TestObjectId[],
    auditFilterSpec?: any,
    testRunFilterSpec?: any,
    fulltextFilter?: string,
}
export type FindingGroupingSpec = string[];


export interface TestSessionInfo {
    id: TestSessionId,
    creator: UserId,
    participants: UserId[],
    projectId: ProjectId,
    name: string,
    creationTime: Date | undefined,
    endTime: Date | undefined,
    latestActivity: Date | undefined,
    executionStatus: TestSessionEvaluationStatus,
    evaluationStatus: TestSessionEvaluationStatus,
    executionStatistics: { [s: string /* TestExecutionEvaluationStatus */]: number }
}

export interface AdHocReportSpec {
    from?: number,
    size?: number,
    filterSpec: FilterSpec,
    groupingSpec: GroupingSpec,
    aggregationSpec: GroupingSpec
}

export interface DateHistogramSpec {
    interval: string,
    format: string,
    timeField: string,
    aggField: string[]
}

export interface FindingsQuerySpec {
    from?: number,
    size?: number,
    filterSpec: FindingFilterSpec,
    groupingSpec?: FindingGroupingSpec,
    aggregationSpec?: FindingGroupingSpec,
    addSeverityAggregation?: boolean,
    dateHistogramSpecs?: DateHistogramSpec[],
}

export interface FindingsQueryResult {
    aggregations: TestReportQueryAggregation[],
    items: FindingSearchRecord[]
    totalHits: number
}

export interface TestReportSpec {
    id: ReportSpecId,
    name: string,
    projectId: ProjectId,
    creator: UserId,
    creationTime: DateTime,
    filterSpec: FilterSpec,
    groupingSpec: GroupingSpec,
    aggregationSpec: GroupingSpec,
    shared: boolean
}

export interface TestReportQueryAggregation {
    buckets: {
        key: string;
        count: number;
        subAggregations: TestReportQueryAggregation[];
    }[];
    name: string;
}

export interface TestReportQueryResult {
    data: {
        aggregations: TestReportQueryAggregation[],
        items: TestRunReport[]
    };
}

export interface TestEmailAccount {
    id: TestEmailAccountId,
    emailAddress: string,
    creator: UserId,
    projectId: ProjectId,
    optTestRunId?: TestRunId,
    optAuditId?: AuditId,
    creationDate: DateTime,
    expirationDate: DateTime
}

// TODO

export interface NamedUrl {
    name?: string,
    url: string
}

export type ExplorationConfig = {
    //case class ExplorationConfig(maxStates: Option[Int], maxDepth:Option[Int], maxWidth: Option[Int], maxCrawlDuration: Duration, prioritizingStrategy: PrioritizingStrategy, explorationStrategy: URLExtractionStrategy)
    maxStates?: number,
    maxDepth?: number,
    maxWidth?: number,
    maxCrawlDuration: string,
    prioritizingStrategy: 'DepthFirst' | 'BreadthFirst',
    explorationStrategy: {
        restrictToSeedHosts: boolean,
        useAnchorTags: boolean,
        useFormActions: boolean,
        useClickableElements: boolean
    }
}

export type StateGeneratorSpec = {
    type: 'URLListGeneratorSpec',
    urls: NamedUrl[],
} | {
    type: 'SiteMapGeneratorSpec',
    startUrl: string,
} | {
    type: 'ExplorationSpec',
    seed: {
        urls: NamedUrl[],
        useSitemap: boolean
    },
    strategy: ExplorationConfig,
};

export interface NewStateCollectorJobSpec {
    stateGenerators: StateGeneratorSpec[],
    name?: string,
    vehicleSpec?: VehicleSpecification,
    requestedFacts?: FactRequest[],
    requestSpec?: any // TODO
    maxParallelExpeditions?: number
}

export type StateGeneratorJobInfo = any;

export interface StateCollection {
    id: StateCollectionId,
    name?: string,
    states: BrowserSessionStateId[],
    collectorJob?: StateCollectorJobId,
    collectorSpec?: StateCollectorSpecId,
    creator: UserId,
    creationTime: DateTime,
    projectId: ProjectId
}

export interface StateCollectorJobProgress {
    progressMessage: string,
    progress: Progress | number,
    failureMsg?: string
}

export type StateCollectorJobStatus = 'running' | 'finished' | 'failed';

export interface StateCollectorJob {
    id: StateCollectorJobId,
    creationTime: DateTime,
    lastUpdateTime: DateTime,
    creator: UserId,
    spec: StateCollectorSpecId,
    stateGeneratorJobs: StateGeneratorJobInfo[],
    stateCollectionId: StateCollectionId,
    projectId: ProjectId,
    status: StateCollectorJobStatus,
    progress: StateCollectorJobProgress
}

export interface StateCollectorSpec {
    id: StateCollectorSpecId,
    name?: string,
    creationTime: DateTime,
    creator: UserId,
    projectId: ProjectId,
    vehicleSpec: VehicleSpecification,
    stateGenerators: StateGeneratorSpec[]
}


export interface TestSessionNotebook {
    sessionId: TestSessionId
    cells: Array<TestSessionNotebookCell>
}

export interface TestSessionNotebookCell {
    id: TestSessionCellId;
    cellType: string;
    input: any;
    inputHistory: any[];
    cellRuns: CellRun[];
}

export interface CellRun {
    id: string;
    runSeq: number;
    assignments: any;
    on: DateTime;
    by: UserId;
}

export interface TestSessionNotebookCellInsertDto {
    cellType: string,
    predecessor: TestSessionCellId,
    input: any,
    append?: boolean
}

// A fact is either an artifact or a test result
export type FactType = { artifactType: string } | { findingType: string }

export interface FactRequest {
    factType: FactType,
    params?: any
}
